import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../../share/util/api/polling';
import { executeSyncRequest } from '../../../../../share/util/api/syncRequest';
import apiService from '../../../../../share/util/api/service';
import handler from '../../../../../share/util/apiResultValidator';
import WS45Dialog from '../Requirement045Dialog';
import requirement045Utils from './Requirement045Utils';
import individualWorkSupportUtil from '../../../../../share/util/individualWorkSupportUtil';
import diagnoseResource from '../../../../../share/util/diagnose';
import resource from '../../../../../share/util/resourceMap';
import session from '../../../../../share/session';
import { computeObdOmtDTCTableItem } from '../../../../../../src/util/obd/diag';
import { createModelDisplaySpec, createModelDisplayNameCurrentLanguage } from '../../../../../share/util/vehicle';
import { outputRegistrationNumber, createRegistrationNumber } from "../../../../../util/vinInfoUtils";

const MID_TID_PAIRS = requirement045Utils.getMidTidPairs(true); // "$" プレフィックス付きで取得

/**
 * [obd] オンボードモニタテスト
 * [WSP] オンボードモニタテスト
 * @param {instance} self 呼び出し元this
 */
export async function getOnboardMonitorTest(self) {
  try {
    self.isLoading = true;

    switch (self.workSupportId) {
      case 4501:
        await handleObdMonitorTest(self);
        break;

      case 12501:
        await handleWspMonitorTest(self);
        break;

      default:
        throw new Error(`Unsupported workSupportId: ${self.workSupportId}`);
    }
  } catch (error) {
    await requirement045Utils.exceptionHandler(requirement045Utils.METHODS.GET_ONBOARD_MONITOR_TEST, error, self);
  } finally {
    requirement045Utils.terminationHandler(self);
  }
}

/**
 * OBD オンボードモニタテスト処理
 * @param {instance} self 呼び出し元this
 */
async function handleObdMonitorTest(self) {
  const response = await requirement045Utils.doPolling(
    self,
    apiService.OBD,
    {
      is_called_from_work_support: true,
      inquiry_mids: [0x3c, 0x3d]
    },
    `/netApps/${self.netAppId}/obd/${self.obdDataId}/systems/${self.systemNo}/onboardMonitorTest`,
    `/netApps/${self.netAppId}/requests/`
  );
  await processMonitorTestResponse(self, response, true);
}

/**
 * WSP オンボードモニタテスト処理
 * @param {instance} self 呼び出し元this
 */
async function handleWspMonitorTest(self) {
  const token = session.getToken();
  const requestBody = {
    id_token: token.id_token,
    type: 'read_dtc',
    obd_data_id: self.obdDataId,
    system_no: self.systemNo
  };

  const response = await individualWorkSupportUtil.pollingWspApi(
    self,
    self.netAppId,
    self.systemId,
    self.workSupportId,
    requestBody,
    (errorType) => { errorType; self.showUnexpectedError(true); },
    (result) => self.validateErrorResult(result),
    (result) => self.validateErrorResult(result)
  );
  await processMonitorTestResponse(self, response, false);
}

/**
 * オンボードモニタテストレスポンス処理
 * @param {instance} self 呼び出し元this
 * @param {object} response APIレスポンス
 * @param {boolean} isObd OBDかどうか
 */
async function processMonitorTestResponse(self, response, isObd) {
  if (!response) return;
  if (!response.data.status) {
    throw new Error(`Invalid response: ${response}`);
  }

  if (response.data.status === REQUEST_STATUS_COMPLETED) {
    self.showResultOnboardMeasuredId = isObd
      ? response.data?.results?.measure_id
      : response.data.user_data.measured_id;
    await self.getDiagCode();
  } else if (response.data.status === REQUEST_STATUS_FAILED) {
    self.showResultOnboardErrorCode = response.data?.error_code;
    if (!(self.showResultOnboardErrorCode === "" || self.showResultOnboardErrorCode === "not_support")) {
      throw new Error(`Invalid error_code: ${self.showResultOnboardErrorCode}`);
    }
    await self.getDiagCode();
  } else {
    throw new Error(`Unexpected status: ${response.data.status}`);
  }
}

/**
 * [diag] ダイアグコード取得
 * @param {instance} self 呼び出し元this
 */
export async function getDiagCode(self) {
  try {
    self.isLoading = true;
    const response = await requirement045Utils.doPolling(
      self,
      apiService.DIAG,
      { is_called_from_work_support: true },
      `/netApps/${self.netAppId}/systems/${self.systemId}/diagDtc`,
      `/netApps/${self.netAppId}/systems/${self.systemId}/requests/`,
      false,
      (pollingStopTimeOutFlg) => {
        if (pollingStopTimeOutFlg) {
          // タイムアウト停止
          self.setIsTimeoutLocked(true);
          self.stopSessionTimer();
        } else {
          // タイムアウト停止の終了
          self.resetSessionTimer();
          self.setIsTimeoutLocked(false);
        }
      },
      process.env.VUE_APP_INDIVIDUAL_DTC_RETRY_COUNT,
      process.env.VUE_APP_INDIVIDUAL_DTC_RETRY_INTERVAL,
    );
    if (response) {
      if (response.data.status === REQUEST_STATUS_COMPLETED) {
        // 正常終了 : プロジェクトデータ保存画面の表示
        self.dtcMeasuredId = response.data.measured_id;
        await self.screenMovement();
      } else {
        throw new Error('Unexpected error occurred: ', response.data);
      }
    }
  } catch (error) {
    await requirement045Utils.exceptionHandler(requirement045Utils.METHODS.GET_DIAG_CODE, error, self);
  } finally {
    requirement045Utils.terminationHandler(self);
  }
}

/**
 * プロジェクトデータ保存画面 表示情報取得
 * @param {instance} self 呼び出し元this
 */
export function screenMovement(self) {
  try {
    self.isLoading = true;
    /** メモ情報のクリア */
    self.diagnoseComment = self.memo = '';
    // プロジェクト保存画面に必要な情報を設定
    const vehicle = self.vehicleInfo.vehicle;
    self.vin = vehicle.vin.vin;
    self.displayProjectID = vehicle.project.id;
    // 車種情報から表示名称を取得
    const spec = createModelDisplaySpec(vehicle.vin.type, vehicle.spec);
    self.vehicleName = createModelDisplayNameCurrentLanguage(self.vehicleInfo, self.vehicleModelDisplayNameList, self.language, '{0} {1}', spec);
    // 海外VINの場合、登録番号は画面から非表示にする
    if (self.hideRegistrationNumber) {
      if (outputRegistrationNumber(vehicle.vin)) {
        self.registrationNumber = createRegistrationNumber(vehicle.vin);
      }
      else {
        self.registrationNumber = "";
      }
    }
    self.$nextTick(() => { self.dialog.phase = WS45Dialog.PHASE.PROJECT_DATA; });
  } finally {
    requirement045Utils.terminationHandler(self);
  }
}

/**
 * [wsp] プロジェクトデータ保存
 * @param {instance} self 呼び出し元this
 */
export async function update(self) {
  self.isLoading = true;
  const endPoint = `/projects/${self.projectId}/${self.measuredId}`;
  const reqBody = {
    comment: self.$refs.projectData.inputDiagnoseComment,
    measured: {
      name: self.$refs.projectData.inputDataName,
      memo: self.$refs.projectData.inputMemo
    },
    linked_id: {
      obd: self.obdDataId,
      onboard_monitor_test: self.showResultOnboardMeasuredId ? self.showResultOnboardMeasuredId : "null",
      dtc: self.dtcMeasuredId,
    }
  };
  await apiService.postCall(apiService.WSP, endPoint, reqBody, self.operationScreen);
  // 結果にかかわらず[wsp] オンボードモニタテスト結果取得へ遷移
  await self.getMonitorTestData();
}

/**
 * [wsp] オンボードモニタテスト結果取得
 * @param {instance} self 呼び出し元this
 */
export async function getMonitorTestData(self) {
  try {
    if (self.isMonitorTestInitial && self.showResultOnboardErrorCode !== "not_support") {
      // 初回実行のみデータ取得
      self.isMonitorTestInitial = false;
      self.isLoading = true;
      // バックエンドAPI GETのみ
      self.getMonitorTestDataResponse = await requirement045Utils.doSyncRequest(
        self,
        apiService.OBD,
        `/obd/${self.obdDataId}/measures/${self.showResultOnboardMeasuredId}/onboardMonitorTest`
      );
    }
    if (self.showResultOnboardErrorCode === "not_support") {
      self.dialog.phase = WS45Dialog.PHASE.MONITOR_TEST_RESULT;
      monitorTestResultTableDataConvert(self, []);
      return;
    }
    // 正常コールバック
    if (self.getMonitorTestDataResponse) {
      // 正常終了 : モニタテスト結果表示画面へ遷移
      self.dialog.phase = WS45Dialog.PHASE.MONITOR_TEST_RESULT;
      // モニタテスト結果一覧作成処理
      monitorTestResultTableDataConvert(
        self,
        self.getMonitorTestDataResponse.data.user_data.results
      );
    }
  } catch (error) {
    await requirement045Utils.exceptionHandler(requirement045Utils.METHODS.GET_MONITOR_TEST_DATA, error, self);
  } finally {
    requirement045Utils.terminationHandler(self);
  }
}

/**
 * [diag] 計測データ(DTC)取得
 * @param {instance} self 呼び出し元this
 */
export async function getDiagMeasuredData(self) {
  try {
    if (self.isDiagInitial) {
      // 初回実行のみデータ取得
      self.isDiagInitial = false;
      self.isLoading = true;
      // バックエンドAPI GETのみ
      self.getDiagMeasuredDataResponse = await executeSyncRequest(
        self,
        async () => {
          return await apiService.getCall(
            apiService.DIAG,
            `/diagDtc/${self.projectId}/${self.dtcMeasuredId}/dtc/${self.systemId}`,
            self.operationScreen
          );
        },
        // エラー発生のコールバック
        (errorType) => { errorType; self.showUnexpectedError(true); },
        // GET API で正常レスポンスが返ってきた際の内容チェックはデフォルト処理を利用
        undefined,
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する
        (result) => { self.validateErrorResult(result); },
      );
      // 正常コールバック
      if (self.getDiagMeasuredDataResponse) {
        // 正常終了 : DTC表示画面へ遷移
        self.dialog.phase = WS45Dialog.PHASE.DTC_DISPLAY;
        // DTC結果一覧作成処理
        readDtcTableDataConvert(self, self.getDiagMeasuredDataResponse.data.data);
      }
    } else {
      // 正常終了 : DTC表示画面へ遷移
      self.dialog.phase = WS45Dialog.PHASE.DTC_DISPLAY;
      // DTC結果一覧作成処理
      readDtcTableDataConvert(self, self.getDiagMeasuredDataResponse.data.data);
    }
  } catch (error) {
    await requirement045Utils.exceptionHandler(requirement045Utils.METHODS.GET_DIAG_MEASURED_DATA, error, self);
  } finally {
    requirement045Utils.terminationHandler(self);
  }
}

/**
 * DTC表示画面 FFDボタン押下時処理
 * @param {instance} self 呼び出し元this
 * @param {object} data DTC表示FFDボタン押下行のレコード情報
 */
export async function rowButtonClickEvent(self, data) {
  if (!data) return;
  // タイトルの設定
  const status = data.status ? ` (${self.$t(data.status)}) ` : '';
  self.ffdTitle = data.dtc + status;
  self.ffdSystem = self.$t(data.name);
  self.ffdDescription = self.$t(data.description);
  const cache = self.cachedFFDs.filter(e => e.system === data.systemID && e.dtc === data.dtc);
  if (cache.length > 0) {
    // キャッシュ有りならキャッシュ済みデータを表示
    self.ffdSystemTable = cache[0].data;
    self.selectedSystem = data;
    self.dialog.phase = WS45Dialog.PHASE.DETAILED_FFD;
  } else {
    // キャッシュなしなら FFD 一覧情報を取得
    // 選択された任意のシステムのFFD一覧情報を取得
    const endPoint = `/diagDtc/${self.projectId}/${self.dtcMeasuredId}/ffd/${self.systemId}/${data.dtc}`;
    self.isLoading = true;
    const res = await apiService.getCall(apiService.DIAG, endPoint, self.operationScreen);
    self.isLoading = false;
    handler.validate(handler.validateTypes.all, res, self, null, () => {
      // 取得情報のキャッシュ
      self.cachedFFDs.push({
        system: data.systemID,
        dtc: data.dtc,
        data: res.data.data
      });
      // FFDダイアログの表示
      self.ffdSystemTable = res.data.data;
      self.selectedSystem = data;
      self.dialog.phase = WS45Dialog.PHASE.DETAILED_FFD;
    }, (result) => {
      // 処理が失敗したときのコールバック処理
      self.validateErrorResult(result);
    }, null, false);
  }
}

/** 
 * モニタテスト結果一覧作成処理 
 * @param {instance} self 呼び出し元this
 * @param {array} onboardMonitorTest モニタテスト読出し結果
 */
function monitorTestResultTableDataConvert(self, monitorTest) {
  const tableInfo = [
    { name: 'scaling_id', label: 'individual.label_table_scaling_id' },
    { name: 'test_value', label: 'individual.label_table_value' },
    { name: 'unit', label: 'individual.label_table_unit' },
    { name: 'minimum_test_limit', label: 'individual.label_table_min' },
    { name: 'maximum_test_limit', label: 'individual.label_table_max' },
    { name: 'result', label: 'individual.label_table_result' }
  ];

  // WS.45/125個別処理
  let filteredTestResults = monitorTest;
  if (self.workSupportId === 4501) {
    tableInfo.unshift(
      { name: 'mid', label: 'individual.label_table_mid' },
      { name: 'tid', label: 'individual.label_table_tid' }
    );
    // MID: MID_TID_PAIRSに含まれるペアのみのデータにフィルター
    filteredTestResults = monitorTest.filter(item => {
      const targetMids = MID_TID_PAIRS.filter(element => element[0] === item.mid);
      return targetMids.some(element => element[1] === item.tid);
    });
  } else {
    tableInfo.unshift(
      { name: 'status', label: 'message.header_vehicle_dtc_table_status' },
      { name: 'code', label: 'message.header_vehicle_dtc_table_code', originalSortFunc: diagnoseResource.sortFuncCode },
      { name: 'description', label: 'message.header_vehicle_dtc_table_description', useResource: true, originalSortFunc: diagnoseResource.sortFuncDescription }
    );
  }

  // カラムを設定
  self.displayTableColumns = tableInfo;

  // テーブルデータを設定
  self.$nextTick(() => {
    self.displayTableInfo = computeObdOmtDTCTableItem(filteredTestResults, self.workSupportId === 12501, self, true);
  });
}

/** DTC一覧作成処理
 * @param {instance} self 呼び出し元this
 * @param {array} readDtcList DTC読出し結果
 */
export function readDtcTableDataConvert(self, readDtcList) {
  try {
    self.displayTableColumns = [
      { name: "status", label: "message.header_vehicle_dtc_table_status", useResource: true },
      { name: 'dtc', label: "message.header_vehicle_dtc_table_code", originalSortFunc: diagnoseResource.sortFuncCode },
      { name: 'description', label: "message.header_vehicle_dtc_table_description", useResource: true, originalSortFunc: diagnoseResource.sortFuncDescription },
      { name: "ffd", label: "message.header_vehicle_dtc_table_ffd", useButton: true, originalSortFunc: diagnoseResource.sortFuncFfd }
    ];
    // テーブル表示情報初期化
    self.displayTableInfo = [];
    let filteredDtcSystems = [];
    // 対象DTCでフィルタ
    filteredDtcSystems = readDtcList
      .filter(dtc => requirement045Utils.getAllTargetDtcList(self).includes(dtc.code))
      .filter(dtc => dtc.status_mask.pending || dtc.status_mask.confirmed);

    // テーブル作成
    const targetDtcList = resource.createDtcTable(filteredDtcSystems, self.systemId, self, true);
    self.$nextTick(() => {
      self.displayTableInfo = self.displayTableInfo.concat(targetDtcList);
    });
  } catch (error) {
    requirement045Utils.exceptionHandler(requirement045Utils.METHODS.READ_DTC_TABLE_DATA_CONVERT, error, self);
  }
}

/** レディネスコード一覧作成処理
 * @param {instance} self 呼び出し元this
 * @param {array} readinessCodeList DTC読出し結果
 */
export function readinessCodeTableDataConvert(self, readinessCodeList) {
  try {
    self.displayTableColumns = [
      { name: "status", label: "message.header_vehicle_dtc_table_status", useResource: true },
      { name: "dtc", label: "message.header_vehicle_dtc_table_code", originalSortFunc: diagnoseResource.sortFuncCode },
      { name: "description", label: "message.header_vehicle_dtc_table_description", useResource: true, originalSortFunc: diagnoseResource.sortFuncDescription },
    ];
    // テーブル表示情報初期化
    self.displayTableInfo = [];
    let filteredDtcSystems = [];
    // 対象DTCでフィルタ
    filteredDtcSystems = readinessCodeList
      .filter(dtc => self.additionalInfo.target_dtc_list.includes(dtc.code))
      .filter(dtc => dtc.status_mask.not_completed);

    // テーブル作成
    const targetDtcList = resource.createDtcTable(filteredDtcSystems, self.systemId, self, true);
    for (let i = 0; i < targetDtcList.length; i++) {
      if (targetDtcList[i].status !== 'message.j_status_vehicle_dtc_status_no_dtc') {
        targetDtcList[i].status = `individual.label_readiness_code`;
      }
    }
    self.$nextTick(() => {
      self.displayTableInfo = self.displayTableInfo.concat(targetDtcList);
    });
  } catch (error) {
    requirement045Utils.exceptionHandler(requirement045Utils.METHODS.READINESS_CODE_TABLE_DATA_CONVERT, error, self);
  }
}

export default {
  getOnboardMonitorTest,
  getDiagCode,
  screenMovement,
  update,
  getMonitorTestData,
  getDiagMeasuredData,
  rowButtonClickEvent,
  monitorTestResultTableDataConvert,
  readDtcTableDataConvert,
  readinessCodeTableDataConvert
};  